import { useEffect, useRef, useState } from "react";

import { useAsset, useProjectAsset } from "../../../../../../../hooks/query";
import { useNotification } from "../../../../../../../hooks";
import { MAX_FILE_SIZE } from "../utils";

import type { IProjectAsset } from "../../../../../../../interfaces";

export type UploadStatus = "queued" | "uploading" | "processing" | "done" | "error";

/**
 * Only the browser-to-API transfer reports real bytes, and against a fast
 * connection that finishes almost immediately. Storing the file and building
 * image variants takes the bulk of the wait but emits nothing, so the transfer
 * is scaled into the first slice of the bar and the rest is estimated.
 */
const TRANSFER_SHARE = 0.75;
const PROCESSING_CEILING = 99;
const TICK_MS = 100;
/** Percentage points the displayed value may move per tick. */
const MAX_STEP = 4;
/** Share of the remaining gap the estimate closes per tick while processing. */
const PROCESSING_EASE = 0.02;

export interface IUploadProgress {
    name: string;
    /** Progress of the whole operation, 0-100, eased toward `target`. */
    percent: number;
    /** Where the progress is currently heading. */
    target: number;
    loaded: number;
    total: number;
    status: UploadStatus;
}

export default function useProjectAttachments(projectId?: string) {
    const [uploadQueue, setUploadQueue] = useState<IUploadProgress[]>([]);
    const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null);

    const { useList, useUpload, useDelete } = useProjectAsset();
    const { useGetAsset } = useAsset();
    const { showError, showSuccess } = useNotification();

    const listSvc = useList(projectId);
    const uploadSvc = useUpload();
    const deleteSvc = useDelete();
    const getAssetSvc = useGetAsset();

    // Tracked by position rather than name, since one batch can hold duplicates.
    const updateQueueEntry = (index: number, patch: Partial<IUploadProgress>) => {
        setUploadQueue((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    };

    const stopTicker = () => {
        if (progressTimer.current) {
            clearInterval(progressTimer.current);
            progressTimer.current = null;
        }
    };

    useEffect(() => stopTicker, []);

    /**
     * Animates the displayed value toward its target rather than snapping to it,
     * so an instant transfer still reads as a moving bar. While the server works
     * the target itself eases toward the ceiling without ever arriving, which
     * keeps the number inching for however long the request takes.
     */
    const startTicker = (index: number) => {
        stopTicker();
        progressTimer.current = setInterval(() => {
            setUploadQueue((current) =>
                current.map((item, i) => {
                    if (i !== index) return item;

                    const target =
                        item.status === "processing"
                            ? item.target + (PROCESSING_CEILING - item.target) * PROCESSING_EASE
                            : item.target;

                    return { ...item, target, percent: Math.min(target, item.percent + MAX_STEP) };
                }),
            );
        }, TICK_MS);
    };

    const uploadFiles = async (files: FileList | null) => {
        if (!files?.length || !projectId) return;

        const accepted = Array.from(files).filter((file) => {
            if (file.size > MAX_FILE_SIZE) {
                showError(`${file.name} is larger than 20 MB and was skipped.`);
                return false;
            }
            return true;
        });
        if (!accepted.length) return;

        setUploadQueue(
            accepted.map((file) => ({
                name: file.name,
                percent: 0,
                target: 0,
                loaded: 0,
                total: file.size,
                status: "queued" as UploadStatus,
            })),
        );
        let uploaded = 0;

        // Sequential so a large batch does not open a request per file at once.
        for (let index = 0; index < accepted.length; index += 1) {
            const file = accepted[index];
            updateQueueEntry(index, { status: "uploading" });
            startTicker(index);

            try {
                await uploadSvc.mutateAsync({
                    ProjectId: projectId,
                    File: file,
                    onProgress: ({ loaded, total, percent }) =>
                        updateQueueEntry(index, {
                            loaded,
                            total,
                            target: percent * TRANSFER_SHARE,
                            status: percent >= 100 ? "processing" : "uploading",
                        }),
                });
                updateQueueEntry(index, { percent: 100, target: 100, status: "done" });
                uploaded += 1;
            } catch (e: any) {
                updateQueueEntry(index, { status: "error" });
                showError(e?.message || `Failed to upload ${file.name}.`);
            } finally {
                stopTicker();
            }
        }

        setUploadQueue([]);

        if (uploaded) {
            showSuccess(uploaded === 1 ? "File uploaded" : `${uploaded} files uploaded`);
        }
    };

    // The shared download endpoint names the file after its S3 key, so the blob is
    // saved here instead to keep the name the user originally uploaded.
    const downloadAttachment = async (asset: IProjectAsset) => {
        try {
            const blob = await getAssetSvc.mutateAsync({ AssetId: asset.AssetId });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = blobUrl;
            link.download = asset.Title || "attachment";
            document.body.appendChild(link);
            link.click();

            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(link);
        } catch (e: any) {
            showError(e?.message || "Failed to download file.");
        }
    };

    const deleteAttachment = async (asset: IProjectAsset) => {
        if (!projectId) return false;
        try {
            await deleteSvc.mutateAsync({ Id: asset.Id, ProjectId: projectId });
            showSuccess("Attachment deleted");
            return true;
        } catch (e: any) {
            showError(e?.message || "Failed to delete attachment.");
            return false;
        }
    };

    return {
        attachments: listSvc.data || [],
        isLoading: listSvc.isPending,
        uploadQueue,
        isUploading: uploadQueue.length > 0,
        isDeleting: deleteSvc.isPending,
        uploadFiles,
        downloadAttachment,
        deleteAttachment,
    };
}
